import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { adminService } from '../../firebase/adminService';

const Tables = () => {
  const [tables, setTables] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTable, setNewTable] = useState({
    name: '',
    capacity: '',
    type: 'temporal'
  });

  const fetchTables = async () => {
    try {
      setLoading(true);
      const tablesData = await adminService.tables.getAll();
      setTables(tablesData);
      setError(null);
    } catch (error) {
      console.error('Error fetching tables:', error);
      setError('Error al cargar las mesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const handleAddTable = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      // Verificar si el nombre ya existe
      const existingTable = tables.find(
        table => table.name.toLowerCase() === newTable.name.toLowerCase() && 
                table.id !== newTable.id
      );
      
      if (existingTable) {
        setError('Ya existe una mesa con este nombre');
        return;
      }

      if (newTable.id) {
        // Actualización
        await adminService.tables.update(newTable.id, {
          name: newTable.name,
          capacity: parseInt(newTable.capacity),
          type: newTable.type
        });
      } else {
        // Nueva mesa
        await adminService.tables.create({
          name: newTable.name,
          capacity: parseInt(newTable.capacity),
          type: newTable.type
        });
      }
      
      await fetchTables();
      setShowAddModal(false);
      setNewTable({ name: '', capacity: '', type: 'publico' });
    } catch (error) {
      console.error('Error managing table:', error);
      setError(error.message || 'Error al gestionar la mesa');
    }
  };
  const getSortedTables = () => {
    return [...tables].sort((a, b) => {
      // Extraer números de los nombres de las mesas
      const numA = parseInt(a.name.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.name.replace(/[^0-9]/g, ''));
      
      // Si ambos nombres contienen números, ordenar por número
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Si solo uno contiene número o ninguno, ordenar alfabéticamente
      return a.name.localeCompare(b.name);
    });
  };
  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta mesa?')) {
      return;
    }

    try {
      setError(null);
      await adminService.tables.delete(tableId);
      await fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      setError('Error al eliminar la mesa');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestión de Mesas</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          <Plus size={20} />
          Añadir Mesa
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {getSortedTables().map((table) => (
          <div key={table.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">{table.name}</h3>
                <p className="text-gray-500">Capacidad: {table.capacity} personas</p>
                <p className="text-gray-500 capitalize">Tipo: {table.type}</p>
              </div>
              <div className="flex gap-2">
                <button 
                  className="p-2 text-blue-500 hover:bg-blue-50 rounded"
                  onClick={() => {
                    setNewTable({
                      id: table.id,
                      name: table.name,
                      capacity: table.capacity,
                      type: table.type
                    });
                    setShowAddModal(true);
                  }}
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                  onClick={() => handleDeleteTable(table.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <div className={`inline-flex px-3 py-1 rounded-full text-sm
              ${table.status === 'available' ? 
                'bg-green-100 text-green-800' : 
                'bg-red-100 text-red-800'
              }`}>
              {table.status === 'available' ? 'Disponible' : 'Ocupada'}
            </div>
          </div>
        ))}
      </div>

      {/* Modal para añadir/editar mesa */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {newTable.id ? 'Editar Mesa' : 'Añadir Nueva Mesa'}
            </h2>
            <form onSubmit={handleAddTable}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre de Mesa
                  </label>
                  <input
                    type="text"
                    value={newTable.name}
                    onChange={(e) => setNewTable({...newTable, name: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                    placeholder="Ej: Mesa 1, Terraza 2, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Capacidad
                  </label>
                  <input
                    type="number"
                    value={newTable.capacity}
                    onChange={(e) => setNewTable({...newTable, capacity: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                    min="1"
                    placeholder="Número de comensales"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tipo
                  </label>
                  <select
                    value={newTable.type}
                    onChange={(e) => setNewTable({...newTable, type: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="publico">Público</option>
                    <option value="temporal">Temporal</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewTable({ name: '', capacity: '', type: 'publico' });
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {newTable.id ? 'Guardar Cambios' : 'Añadir Mesa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tables;